import React, { useEffect, useRef, useState } from "react";
import "./FoodAllowance.css";
import logo from "../../assets/WHL-LOGO.png";
import UpdateFoodDetail from "./UpdateFoodDetail";
import axiosInstance from "../../services/auth";
import { toast, ToastContainer } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import { useCombinedContext } from "../../context/CombinedContext";
import { useFoodAllowanceContext } from "./FoodAllowanceList";

const ViewReport = ({
  formatPrice,
  foodallowance,
  onClose,
  companyId,
  onUpdate,
}) => {
  const { selectedCompany } = useCombinedContext();
  const company = selectedCompany;
  const { fetchFoodAllowance, filterData } = useFoodAllowanceContext();

  // const { filterData } = useFoodAllowanceContext();
  const [foodDetails, setFoodDetails] = useState(
    foodallowance.foodDetails || [],
  );

  useEffect(() => {
    const updatedRecord = filterData.find((fa) => fa._id === foodallowance._id);
    if (updatedRecord) {
      setFoodDetails(updatedRecord.foodDetails || []);
    }
  }, [filterData, foodallowance._id]);

  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedFoodDetail, setSelectedFoodDetail] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Print setup
  const printRef = useRef(null);
  const handlePrint = useReactToPrint({ content: () => printRef.current });

  // Prevent background scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleOutsideClick = (e) => {
    if (e.target.classList.contains("modal")) onClose();
  };

  const toggleSortOrder = () =>
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));

  // 3) Sort *state*:
  const sortedFoodDetails = [...foodDetails].sort((a, b) => {
    if (!a.date) return 1;
    if (!b.date) return -1;
    return sortOrder === "asc"
      ? new Date(a.date) - new Date(b.date)
      : new Date(b.date) - new Date(a.date);
  });

  // Totals from *state*:
  const totalRepayment = foodDetails.reduce(
    (sum, cur) => sum + (cur.amount || 0),
    0,
  );
  const balanceRepayment = foodallowance.foodTotal - totalRepayment;

  const handleEditClick = (detail) => setSelectedFoodDetail(detail);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axiosInstance.delete(
        `/foodallowance/${foodallowance._id}/${deleteTarget}`,
      );
      // 4) Remove from state -> immediate UI update
      setFoodDetails((prev) => prev.filter((d) => d._id !== deleteTarget));
      toast.success("Food detail deleted successfully");
      setIsDeleteModalOpen(false);
      if (onUpdate) onUpdate();
    } catch {
      toast.error("Failed to delete food detail");
    }
  };

  const [totals, setTotals] = useState({
    allowanceTotal: 0,
    foodTotal: 0,
    paidTotal: 0,
    balance: 0,
  });

  useEffect(() => {
    let allowanceTotal = 0;
    let foodTotal = 0;
    let paidTotal = 0;

    foodDetails.forEach((item) => {
      allowanceTotal += Number(item.allowance || 0);
      foodTotal += Number(item.amount || 0);
      paidTotal += Number(item.paidAmount || 0);
    });

    const balance = foodTotal - paidTotal;

    setTotals({
      allowanceTotal,
      foodTotal,
      paidTotal,
      balance,
    });
  }, [foodDetails]);

  return (
    <>
      {/* BACKDROP & MAIN MODAL */}
      <div
        className="modal show d-block"
        tabIndex={-1}
        onClick={handleOutsideClick}
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content foodallowance_model">
            <div className="modal-header text-white text-center vesel_form">
              <h5 className="modal-title">Food Allowance Report</h5>
              <button
                type="button"
                className="btn-close text-white"
                onClick={onClose}
              />
            </div>

            {/* PRINTABLE BODY */}
            <div ref={printRef} className="modal-body modalWhite">
              {/* header */}
              <div className="food_head mb-4 d-flex">
                <img
                  src={logo}
                  alt="logo"
                  style={{ width: 125, marginRight: 20 }}
                />
                <div>
                  <h2
                    className="company_font"
                    style={{ textDecoration: "underline" }}
                  >
                    {company.company_name}
                  </h2>
                  <p style={{ fontSize: 12 }}>
                    Co Reg No: {company.co_regno} | GST No: {company.gst_no}
                    <br />
                    {company.address}
                    <br />
                    Tel: {company.telephone} | Fax: {company.fax}
                    <br />
                    {company.email}
                  </p>
                </div>
              </div>
              <hr />

              {/* employee info */}
              <p>
                <strong>Employee Name:</strong> {foodallowance.employee?.name}
              </p>
              <p>
                <strong>Employee ID No:</strong>{" "}
                {foodallowance.employee?.employeeNo}
              </p>

              {/* sort button */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6>Food Details:</h6>
                <button
                  className="button-2 for_print"
                  onClick={toggleSortOrder}
                >
                  Sort by Date:{" "}
                  {sortOrder === "asc" ? "Ascending" : "Descending"}
                </button>
              </div>

              {/* details table */}
              {sortedFoodDetails.length > 0 ? (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Is Eaten</th>
                      <th>Dish</th>
                      <th>Quantity</th>
                      <th>Hours</th>
                      <th>Vessel</th>
                      <th>Amount</th>
                      <th>Allowance</th>
                      <th>Remark</th>
                      <th className="for_print">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedFoodDetails.map((d) => (
                      <tr key={d._id}>
                        <td>
                          {d.date
                            ? new Date(d.date).toLocaleDateString()
                            : "N/A"}
                        </td>
                        <td>{d.isEaten ? "Yes" : "No"}</td>
                        <td>{d.dish || "N/A"}</td>
                        <td>{d.quantity || "N/A"}</td>
                        <td>{d.hours || "N/A"}</td>
                        <td>
                          {d.vessal?.vessel_name || "N/A"},{" "}
                          {d.vessal?.vessel_code || "N/A"}
                        </td>
                        <td>
                          <div className="d-flex justify-content-between">
                            <span>$</span>
                            <span>{formatPrice(d.amount)}</span>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex justify-content-between px-2">
                            <span>$</span>
                            <span>{formatPrice(d.allowance)}</span>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex justify-content-between px-2">
                            <span>{d.remark}</span>
                          </div>
                        </td>
                        <td className="for_print">
                          <div className="d-flex gap-2 justify-content-center">
                            <button
                              className="btn btn-outline-success btn-sm"
                              onClick={() => handleEditClick(d)}
                            >
                              <i className="bi bi-pencil-square" />
                            </button>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => {
                                setDeleteTarget(d._id);
                                setIsDeleteModalOpen(true);
                              }}
                            >
                              <i className="bi bi-trash3" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>No food details available.</p>
              )}

              {/* totals */}
              {/* <p>
                <strong>Total Allowance:</strong> $
                {formatPrice(foodallowance.allowanceTotal)}
              </p>
              <p>
                <strong>Total Amount:</strong> $
                {formatPrice(foodallowance.foodTotal)}
              </p>
              <p>
                <strong>Total Paid Amount:</strong> $
                {formatPrice(foodallowance.paidTotal)}
              </p>
              <p>
                <strong>Balance Amount:</strong> $
                {formatPrice(foodallowance.balance)}
              </p> */}
              <p>
                <strong>Total Allowance:</strong> $
                {formatPrice(totals.allowanceTotal)}
              </p>
              <p>
                <strong>Total Amount:</strong> ${formatPrice(totals.foodTotal)}
              </p>
              {/* <p><strong>Total Paid Amount:</strong> ${formatPrice(totals.paidTotal)}</p> */}
              <p>
                <strong>Total Paid Amount:</strong> $
                {formatPrice(foodallowance.paidTotal)}
              </p>
              <p>
                <strong>Balance Amount:</strong> $
                {formatPrice(totals.balance - foodallowance.paidTotal)}
              </p>
            </div>

            {/* print button */}
            <div className="text-center mb-4">
              <button className="button-2" onClick={handlePrint}>
                Print
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* DELETE CONFIRMATION */}
      {isDeleteModalOpen && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={(e) =>
            e.target.classList.contains("modal") && setIsDeleteModalOpen(false)
          }
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Deletion</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setIsDeleteModalOpen(false)}
                />
              </div>
              <div className="modal-body">
                Are you sure you want to delete this food detail? This action
                cannot be undone.
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setIsDeleteModalOpen(false)}
                >
                  Cancel
                </button>
                <button className="btn btn-danger" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* UPDATE MODAL */}
      {selectedFoodDetail && (
        <UpdateFoodDetail
          foodRecord={foodallowance}
          foodDetail={selectedFoodDetail}
          companyId={companyId}
          onClose={() => setSelectedFoodDetail(null)}
          fetchFoodAllowance={fetchFoodAllowance}
          onUpdate={fetchFoodAllowance}
        />
      )}

      <ToastContainer />
    </>
  );
};

export default ViewReport;
